'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetLeaderboardRow, useAuth, useGetLeaderboard, usePrefetchGetUser } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { userIdentityKey } from '@/components/profile/user-identity-key';
import { parseAvatarPreferences } from '@/components/social/avatar-preferences';
import { LeaderboardEmptyState } from '@/components/social/leaderboard/leaderboard-empty';
import { LeaderboardSkeleton } from '@/components/social/leaderboard/leaderboard-skeleton';
import { TabContent } from '@/components/social/leaderboard/tab-content';
import { useAnchorScroll } from '@/components/social/leaderboard/use-anchor-scroll';
import { mapLeaderboardEntries, readFiniteNumber, readFiniteNumericString } from '@/components/social/leaderboard/use-committed-leaderboard-entries';
import { SocialFeedList } from '@/components/social/shared/social-feed-list';
import type { PnlEntry, PnlSort } from '@/components/social/types';
import { DataTable } from '@/components/ui/data-table';
import { Panel } from '@/components/ui/panel';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { useAppRouter } from '@/lib/network/use-app-router';
import { LB_TRADERS_FIELDS, PORTFOLIO_USER_PROFILE_FIELDS } from '@/lib/sdk/projections';
import { SOCIAL_LEADERBOARD_LIST_PANEL_CLASS } from '@/lib/ui/layout';

// ---------------------------------------------------------------------------
// Profit & Loss tab — consumes the universal DataTable primitive (spec 093).
// Acceptance scenarios from spec 070 US1.
// ---------------------------------------------------------------------------

interface PnlTabProps {
  /** Optional — initial sort applied on mount (preserved from `?sort=` URL param). */
  initialSort?: PnlSort;
  /** Optional — fired when the user clicks a sort header so the URL can sync. */
  onSortChange?: (sort: PnlSort) => void;
  /** Spec 074: when set, fetches the page containing this user's row. */
  anchorUserId?: string | undefined;
  /** Spec 074: increment to scroll the user's row into view + pulse. */
  pulseTick?: number | undefined;
  /** Time window forwarded to the leaderboard hook (`1d`/`1w`/`1m`/`1y`/`lifetime`). */
  timeWindow?: string;
}
function rankForActiveSort(entry: PnlEntry, sortBy: PnlSort): number {
  switch (sortBy) {
    case 'pnl':
      return entry.pnlRank;
    case 'volume':
      return entry.volumeRank;
    case 'trades':
      return entry.tradesRank;
    case 'markets':
      return entry.marketsRank;
  }
}
function mapEntry(item: GetLeaderboardRow, sortBy: PnlSort): PnlEntry | null {
  const userField = (item.user ?? {}) as Record<string, unknown>;
  const providersRaw = Array.isArray(userField.connectedProviders) ? userField.connectedProviders as Array<Record<string, unknown>> : [];
  const totalPnl = readFiniteNumericString(item.totalPnl);
  const totalVolume = readFiniteNumericString(item.totalVolume);
  const totalTrades = readFiniteNumber(item.totalTrades);
  const marketsTraded = item.marketsTraded === null ? null : readFiniteNumber(item.marketsTraded);
  const activeRank = readFiniteNumber(sortBy === 'pnl' ? item.pnlRank : sortBy === 'volume' ? item.volumeRank : sortBy === 'trades' ? item.tradesRank : item.marketsRank);
  const pnlRank = item.pnlRank === null ? activeRank : readFiniteNumber(item.pnlRank);
  const volumeRank = item.volumeRank === null ? activeRank : readFiniteNumber(item.volumeRank);
  const tradesRank = item.tradesRank === null ? activeRank : readFiniteNumber(item.tradesRank);
  const marketsRank = item.marketsRank === null ? activeRank : readFiniteNumber(item.marketsRank);
  if (totalPnl === null || totalVolume === null || totalTrades === null || item.marketsTraded !== null && marketsTraded === null || activeRank === null || pnlRank === null || volumeRank === null || tradesRank === null || marketsRank === null) {
    return null;
  }
  return {
    user: {
      userId: typeof userField.userId === 'string' ? userField.userId : null,
      walletAddress: typeof userField.walletAddress === 'string' ? userField.walletAddress : '',
      displayName: typeof userField.displayName === 'string' ? userField.displayName : null,
      handle: typeof userField.handle === 'string' ? userField.handle : null,
      avatarUrl: typeof userField.avatarUrl === 'string' ? userField.avatarUrl : null,
      avatarNsfw: userField.avatarNsfw === true,
      avatarPreferences: parseAvatarPreferences(userField.avatarPreferences),
      connectedProviders: providersRaw.filter((p): p is {
        provider: 'x' | 'google' | 'apple';
      } & Record<string, unknown> => p.provider === 'x' || p.provider === 'google' || p.provider === 'apple').map(p => ({
        provider: p.provider,
        username: typeof p.username === 'string' ? p.username : '',
        displayName: typeof p.displayName === 'string' ? p.displayName : null
      }))
    },
    totalPnl,
    totalVolume,
    totalTrades,
    marketsTraded,
    pnlRank,
    volumeRank,
    tradesRank,
    marketsRank
  };
}
export function LeaderboardTab(t0) {
  const $ = _c(68);
  const {
    initialSort,
    onSortChange,
    anchorUserId,
    pulseTick,
    timeWindow
  } = t0;
  const tLb = useTranslations("social.leaderboard");
  const {
    user
  } = useAuth();
  const router = useAppRouter();
  const prefetchUser = usePrefetchGetUser();
  const userId = user?.id ?? null;
  const [sortBy, setSortBy] = useState(initialSort ?? "pnl");
  const sortOrder = "desc" as const;
  const leaderboardWindow = timeWindow ?? "lifetime";
  let t1;
  if ($[0] !== onSortChange) {
    t1 = function handleSortChange(next) {
      setSortBy(next);
      if (onSortChange) {
        onSortChange(next);
      }
    };
    $[0] = onSortChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSortChange = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [...LB_TRADERS_FIELDS];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== leaderboardWindow || $[4] !== sortBy) {
    t3 = {
      kind: "traders",
      sortBy,
      sortOrder: "desc",
      window: leaderboardWindow,
      fields: t2
    };
    $[3] = leaderboardWindow;
    $[4] = sortBy;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const leaderboardQuery = useGetLeaderboard(t3);
  const leaderboardStatus = leaderboardQuery.status;
  const leaderboardData = useLiveListData(leaderboardQuery.queryKey);
  const isResolved = leaderboardStatus === "success" && leaderboardQuery.isFetched && !leaderboardQuery.isPlaceholderData;
  let isInitialLoad;
  let rawEntries;
  let t4;
  if ($[6] !== anchorUserId || $[7] !== isResolved || $[8] !== leaderboardData || $[9] !== sortBy) {
    rawEntries = mapLeaderboardEntries(leaderboardData, isResolved, mapEntry, sortBy);
    isInitialLoad = !isResolved && rawEntries.length === 0;
    t4 = anchorUserId !== undefined && rawEntries.some(entry => entry.user.userId === anchorUserId);
    $[6] = anchorUserId;
    $[7] = isResolved;
    $[8] = leaderboardData;
    $[9] = sortBy;
    $[10] = isInitialLoad;
    $[11] = rawEntries;
    $[12] = t4;
  } else {
    isInitialLoad = $[10];
    rawEntries = $[11];
    t4 = $[12];
  }
  const isAnchorLoaded = t4;
  const t5 = leaderboardQuery.hasNextPage === true;
  const t6 = leaderboardQuery.isFetchingNextPage === true;
  let t7;
  if ($[13] !== anchorUserId || $[14] !== isAnchorLoaded || $[15] !== leaderboardQuery.fetchNextPage || $[16] !== pulseTick || $[17] !== t5 || $[18] !== t6) {
    t7 = {
      pulseTick,
      anchorUserId,
      isAnchorLoaded,
      hasNextPage: t5,
      isFetchingNextPage: t6,
      fetchNextPage: leaderboardQuery.fetchNextPage
    };
    $[13] = anchorUserId;
    $[14] = isAnchorLoaded;
    $[15] = leaderboardQuery.fetchNextPage;
    $[16] = pulseTick;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const scrollTick = useAnchorScroll(t7);
  if (isInitialLoad) {
    let t8;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <TabContent><Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="social-leaderboard-panel"><LeaderboardSkeleton columnCount={4} /></Panel></TabContent>;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    return t8;
  }
  if (rawEntries.length === 0) {
    let t8;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <TabContent><Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="social-leaderboard-panel"><LeaderboardEmptyState tab="pnl" /></Panel></TabContent>;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  const t8 = leaderboardQuery.hasNextPage === true;
  const t9 = leaderboardQuery.isFetchingNextPage === true;
  const t10 = leaderboardQuery.fetchNextPage;
  const t11 = leaderboardQuery.isError;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <DataTable.RankColumn />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== tLb) {
    t13 = tLb("trader");
    $[23] = tLb;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13) {
    t14 = <DataTable.IdentityColumn header={t13} />;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== tLb) {
    t15 = tLb("sort_pnl");
    $[27] = tLb;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t15) {
    t16 = <DataTable.SortableColumn sortKey="pnl" header={t15} align="right" className="w-[24%] whitespace-nowrap lg:w-[18%]" />;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== tLb) {
    t17 = tLb("sort_volume");
    $[31] = tLb;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t17) {
    t18 = <DataTable.SortableColumn sortKey="volume" header={t17} align="right" className="w-[24%] whitespace-nowrap lg:w-[18%]" />;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== tLb) {
    t19 = tLb("trades");
    $[35] = tLb;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t19) {
    t20 = <DataTable.SortableColumn sortKey="trades" header={t19} align="right" className="hidden w-[13%] whitespace-nowrap lg:table-cell" hideOnMobile={true} />;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== tLb) {
    t21 = tLb("markets_traded");
    $[39] = tLb;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t21) {
    t22 = <DataTable.SortableColumn sortKey="markets" header={t21} align="right" className="hidden w-[13%] whitespace-nowrap lg:table-cell" hideOnMobile={true} />;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== t14 || $[44] !== t16 || $[45] !== t18 || $[46] !== t20 || $[47] !== t22) {
    t23 = <DataTable.Header>{t12}{t14}{t16}{t18}{t20}{t22}</DataTable.Header>;
    $[43] = t14;
    $[44] = t16;
    $[45] = t18;
    $[46] = t20;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== prefetchUser || $[50] !== router || $[51] !== sortBy || $[52] !== tLb || $[53] !== userId) {
    t24 = <DataTable.Body>{entry_0 => {
        const rank = rankForActiveSort(entry_0, sortBy);
        const entryUserId = entry_0.user.userId ?? "";
        const isUser = userId !== null && entry_0.user.userId === userId;
        const navigate = function navigate() {
          if (entryUserId.length === 0) {
            return;
          }
          router.push(`/portfolio/${entryUserId}`);
        };
        const prefetch = function prefetch() {
          if (entryUserId.length === 0) {
            return;
          }
          prefetchUser({
            identifier: entryUserId,
            fields: [...PORTFOLIO_USER_PROFILE_FIELDS]
          });
        };
        return <DataTable.Row key={userIdentityKey(entry_0.user, `anonymous-rank-${rank}`)} isCurrentUser={isUser} onClick={navigate} onMouseEnter={prefetch} onFocus={prefetch} onPointerDown={prefetch} ariaLabel={tLb("aria_view_profile")}><DataTable.RankCell value={rank} isCurrentUser={isUser} /><DataTable.IdentityCell user={entry_0.user} showYouBadge={isUser} /><DataTable.NumericCell value={Number(entry_0.totalPnl)} format="usdAdaptiveSigned" tone="auto" showThresholdIcon={true} /><DataTable.NumericCell value={Number(entry_0.totalVolume)} format="compact" hideOnMobile={true} /><DataTable.NumericCell value={entry_0.totalTrades} format="int" hideOnMobile={true} className="hidden lg:table-cell" />{entry_0.marketsTraded === null ? <DataTable.TextCell hideOnMobile={true} className="hidden lg:table-cell" tone="tertiary">0</DataTable.TextCell> : <DataTable.NumericCell value={entry_0.marketsTraded} format="int" hideOnMobile={true} className="hidden lg:table-cell" />}</DataTable.Row>;
      }}</DataTable.Body>;
    $[49] = prefetchUser;
    $[50] = router;
    $[51] = sortBy;
    $[52] = tLb;
    $[53] = userId;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== handleSortChange || $[56] !== rawEntries || $[57] !== scrollTick || $[58] !== sortBy || $[59] !== t23 || $[60] !== t24) {
    t25 = <DataTable panel="borderless" desktopRowChrome="feed" entries={rawEntries} sortBy={sortBy} sortOrder={sortOrder} onSortChange={handleSortChange} pulseTick={scrollTick}>{t23}{t24}</DataTable>;
    $[55] = handleSortChange;
    $[56] = rawEntries;
    $[57] = scrollTick;
    $[58] = sortBy;
    $[59] = t23;
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== leaderboardQuery.fetchNextPage || $[63] !== leaderboardQuery.isError || $[64] !== t25 || $[65] !== t8 || $[66] !== t9) {
    t26 = <TabContent><Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="social-leaderboard-panel"><SocialFeedList hasNextPage={t8} isFetchingNextPage={t9} fetchNextPage={t10} isError={t11} data-testid="social-leaderboard-scroll">{t25}</SocialFeedList></Panel></TabContent>;
    $[62] = leaderboardQuery.fetchNextPage;
    $[63] = leaderboardQuery.isError;
    $[64] = t25;
    $[65] = t8;
    $[66] = t9;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  return t26;
}